/** PitchShapes — sing. The ink colour follows the pitch of your voice. */
import type { CreateModule, ModuleContext } from '../../core/module-api';
import { cfgNum, cfgStr } from '../../core/module-api';
import type { Point, RGBA, Shape } from '../../core/scene';
import { defaultStyle, hslToRgba } from '../../core/scene';
import { buildSmoothPath, distance } from '../../core/geometry';

const ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l10-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="16" cy="16" r="3"/></svg>';

/** Map a frequency (Hz) to a hue (0=red … 280=violet) on a log scale. */
function pitchToHue(pitch: number): number {
  const lo = Math.log(90);
  const hi = Math.log(700);
  const p = Math.max(90, Math.min(700, pitch));
  return ((Math.log(p) - lo) / (hi - lo)) * 280;
}

export function createPitch(): CreateModule {
  let segs: { pts: Point[]; hue: number }[] = [];
  let cur: Point[] = [];
  let segHue = 200;
  let drawing = false;

  const reset = (): void => {
    segs = [];
    cur = [];
  };

  return {
    manifest: {
      id: 'pitch',
      kind: 'create',
      name: 'Pitch',
      apiVersion: 1,
      description: 'Sing or hum — the ink colour tracks your pitch, low to high.',
      icon: ICON,
      capabilities: ['audio'],
      params: [
        { kind: 'slider', key: 'width', label: 'Weight', min: 1, max: 16, step: 0.5, default: 3.5 },
        {
          kind: 'select',
          key: 'mode',
          label: 'Colour',
          default: 'palette',
          options: [
            { value: 'palette', label: 'Palette' },
            { value: 'rainbow', label: 'Rainbow' },
          ],
        },
      ],
    },

    onPointerDown(s, ctx) {
      const pitch = ctx.audio?.pitch ?? null;
      segHue = pitch != null ? pitchToHue(pitch) : segHue;
      segs = [];
      cur = [{ x: s.x, y: s.y }];
      drawing = true;
    },

    onPointerMove(s, ctx) {
      if (!drawing) return;
      const p = { x: s.x, y: s.y };
      const last = cur[cur.length - 1];
      if (last && distance(last, p) < 1.4) return;
      const pitch = ctx.audio?.pitch ?? null;
      const hue = pitch != null ? pitchToHue(pitch) : segHue;
      // Split into a new coloured segment once the live pitch drifts far enough
      // from this segment's fixed start hue (do NOT chase it, or it never trips).
      if (Math.abs(hue - segHue) > 18 && cur.length >= 2) {
        segs.push({ pts: cur, hue: segHue });
        cur = [cur[cur.length - 1], p];
        segHue = hue;
      } else {
        cur.push(p);
      }
    },

    onPointerUp() {
      drawing = false;
    },

    getLiveShapes(ctx: ModuleContext): Shape[] {
      const width = cfgNum(ctx.config, 'width', 3.5);
      const usePalette = cfgStr(ctx.config, 'mode', 'palette') === 'palette' && ctx.palette.colors.length >= 2;
      // hue runs 0..280 (see pitchToHue); map to the palette ramp or a fixed rainbow.
      const colorFor = (hue: number): RGBA => (usePalette ? ctx.palette.ramp(hue / 280) : hslToRgba(hue, 0.72, 0.5));
      const mk = (pts: Point[], hue: number): Shape => {
        const style = defaultStyle();
        style.color = colorFor(hue);
        style.lineWidth = width;
        return ctx.newShape({ path: buildSmoothPath(pts), style });
      };
      const out: Shape[] = [];
      for (const seg of segs) {
        if (seg.pts.length >= 2) out.push(mk(seg.pts, seg.hue));
      }
      if (cur.length >= 1) out.push(mk(cur, segHue));
      return out;
    },

    onClear: reset,
    onCommit: reset,
  };
}
